<template>
  <div v-if="!loading" class="d-flex justify-space-around">
    <v-skeleton-loader type="ossein" width="140" height="80" />
  </div>
  <div v-else class="chart-container">
    <div ref="chartContainer" class="lw-chart" />
    <div v-if="data?.timeframe" class="timeframe-indicator">
      {{ data.timeframe }}
    </div>
  </div>
</template>

<script lang="ts" setup>
import { defineProps, ref, watch } from "vue";
import moment from "moment";
import { useQuery } from "@tanstack/vue-query";
import { ISeriesApi, LineSeriesPartialOptions } from "lightweight-charts";

import { useChart } from "@/composables";
import { TransactionHistoricalService } from "@/api/generated";

const props = defineProps({
  saleAddress: {
    type: String,
    required: true,
  },
  height: {
    type: Number,
    default: 200,
  },
});
const loading = ref(true);
const lineSeries = ref<ISeriesApi<"Line">>();

const { data } = useQuery({
  queryFn: () =>
    TransactionHistoricalService.getForPreview({ address: props.saleAddress }),
  queryKey: ["TransactionHistoricalService.getForPreview", props.saleAddress],
  staleTime: 1000 * 60 * 5,
});

const { chartContainer, chart } = useChart({
  height: props.height,
  chartOptions: {
    grid: {
      horzLines: {
        visible: false,
      },
      vertLines: {
        visible: false,
      },
    },
    timeScale: {
      visible: false,
    },
    crosshair: {
      vertLine: {
        visible: false,
      },
      horzLine: {
        visible: false,
      },
    },
    handleScale: false,
  },
  onChartReady: (chart) => {
    const seriesOptions: LineSeriesPartialOptions = {
      priceLineVisible: false,
      color: "rgb(244, 193, 12)",
      lineWidth: 2,
      crosshairMarkerVisible: false,
    };

    const _lineSeries = chart.addLineSeries(seriesOptions);
    _lineSeries?.priceScale().applyOptions({
      visible: false, // disables auto scaling based on visible content
      ticksVisible: false,
    });

    chart?.timeScale().fitContent();
    lineSeries.value = _lineSeries;

    if (data.value?.result?.length) {
      updateSeriesData(data.value);
    }
  },
});

watch(data, (newData) => {
  if (!newData?.result?.length) {
    return;
  }
  lineSeries.value?.setData([]);

  updateSeriesData(newData);
});

function updateSeriesData(data: any[]) {
  let formattedData = data.result
    .map((item) => {
      return {
        time: moment(item.end_time).unix(),
        value: Number(item.last_price),
      };
    })
    .sort((a, b) => a.time - b.time);
  // if formattedData less than 5 generate more data with same value but with time - 1 hour
  if (formattedData.length < 10) {
    for (let i = 0; i < 10 - formattedData.length; i++) {
      const lastItem = formattedData[0];
      formattedData.unshift({
        time: lastItem.time - 3600000,
        value: lastItem.value,
      });
    }
  }
  lineSeries.value?.setData(formattedData);
  chart.value?.timeScale().fitContent();
}
</script>

<style scoped>
.chart-container {
  position: relative;
  margin-right: 8px;

  &,
  .lw-chart {
    height: 100%;
  }

  .timeframe-indicator {
    position: absolute;
    bottom: 0;
    right: 0;
    font-size: 0.6rem;
    text-transform: lowercase;
  }
}
</style>
